import MuiAvatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import type {
  AvatarLook,
  BadgeLook,
  EditorMarkLook,
  EditorMarkPart,
  LogoLook,
  TextLook,
  TextTone as TextToneLook,
} from '@/theme/looks';
import { statusBadgeVariant } from './statusVariants';

const TextRoot = styled(Typography, { name: 'KitText', slot: 'root' })<{ ownerState: TextLook; component?: ElementType }>({});

const VARIANT = {
  pageTitle: 'h4',
  sectionTitle: 'subtitle1',
  title: 'subtitle2',
  body: 'body1',
  small: 'body2',
  label: 'label',
  meta: 'meta',
  metaStrong: 'metaStrong',
  micro: 'micro',
  overline: 'overline',
  tag: 'tag',
  specimenSmall: 'specimenSmall',
  specimen: 'specimen',
  specimenLarge: 'specimenLarge',
} as const;

export type TextVariant = keyof typeof VARIANT;
export type TextTone = Exclude<TextToneLook, 'inherit'>;

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  variant?: TextVariant;
  /** The text colour. Without it the text takes the colour around it. */
  tone?: TextTone;
  /** The element to use. */
  as?: ElementType;
  /** Cuts the text to one line with an ellipsis. */
  truncate?: boolean;
  /** Sets the monospace face with figures of one width. */
  mono?: boolean;
  /** Makes the text a little see-through, for a quiet line on a fill or media. */
  faded?: boolean;
}

/** Shows text in one of the kit's type steps and colour roles. */
export function Text({
  variant = 'body',
  tone,
  as = 'span',
  truncate = false,
  mono = false,
  faded = false,
  ...props
}: TextProps) {
  return (
    <TextRoot
      component={as}
      variant={VARIANT[variant]}
      noWrap={truncate}
      ownerState={{ mono, tone: tone ?? 'inherit', faded }}
      {...props}
    />
  );
}

const BadgeRoot = styled(Chip, { name: 'KitBadge', slot: 'root' })<{ ownerState: BadgeLook; component?: ElementType }>({});

export type BadgeVariant = 'success' | 'warning' | 'info' | 'error' | 'gray';

const COLOR: Record<BadgeVariant, ChipProps['color']> = {
  success: 'success',
  warning: 'warning',
  info: 'info',
  error: 'error',
  gray: 'default',
};

/** Shows a small tonal MUI chip that labels a state. */
export function Badge({
  variant = 'gray',
  sentenceCase = false,
  className,
  children,
}: {
  variant?: BadgeVariant;
  /** Keeps the text as written, not in capitals. */
  sentenceCase?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <BadgeRoot
      component="span"
      variant="tonal"
      color={COLOR[variant]}
      label={children}
      ownerState={{ sentenceCase }}
      className={className}
    />
  );
}

const PULSING = new Set(['active', 'live']);

export interface StatusBadgeProps<Status extends string = string> {
  status: Status;
  /** Translates the status label; without it the raw status is de-underscored. */
  translate?: (status: Status) => string;
  dot?: boolean;
  className?: string;
}

/** Shows a status as a badge in the colour of its state, with an optional dot. */
export function StatusBadge<Status extends string>({ status, translate, dot = true, className }: StatusBadgeProps<Status>) {
  const variant = statusBadgeVariant(status);
  const label = translate ? translate(status) : status.replace(/_/g, ' ');

  return (
    <Badge variant={variant} sentenceCase className={className}>
      {dot && (
        <span
          aria-hidden
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full bg-current',
            PULSING.has(status) && 'pulse-dot',
          )}
        />
      )}
      {label}
    </Badge>
  );
}

const TONE = {
  primary: 'primary.main',
  muted: 'grey.300',
  success: 'success.main',
  warning: 'warning.main',
  danger: 'error.main',
} as const;

export interface DotProps {
  tone?: keyof typeof TONE;
  size?: 'sm' | 'md';
  className?: string;
}

/** Shows a small round mark in a colour role, such as a state beside a label. */
export function Dot({ tone = 'primary', size = 'sm', className }: DotProps) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ bgcolor: TONE[tone] }}
      className={cn('inline-block shrink-0 rounded-full', size === 'sm' ? 'size-1.5' : 'size-2.5', className)}
    />
  );
}

const AvatarRoot = styled(MuiAvatar, { name: 'KitAvatar', slot: 'root' })<{ ownerState: AvatarLook }>({});

export interface AvatarProps {
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Fills the avatar with the brand colour. */
  filled?: boolean;
  className?: string;
}

/** Shows an MUI avatar: the image, or the initials once the image fails to load. */
export function Avatar({ name, src, size = 'md', filled = false, className }: AvatarProps) {
  return (
    <AvatarRoot src={src} alt={name} title={name} ownerState={{ size, filled }} className={className}>
      <span aria-hidden>{initials(name)}</span>
    </AvatarRoot>
  );
}

const LogoRoot = styled('span', { name: 'KitLogo', slot: 'root' })<{ ownerState: LogoLook }>({});

export interface LogoProps {
  className?: string;
}

/** Shows the e-dialog wordmark. */
export function LogoWordmark({ className }: LogoProps) {
  return (
    <LogoRoot ownerState={{ mark: false }} className={className}>
      e&#8288;-&#8288;dialog
    </LogoRoot>
  );
}

/** Shows the e-dialog letter mark. Screen readers skip it. */
export function LogoMark({ className }: LogoProps) {
  return (
    <LogoRoot aria-hidden ownerState={{ mark: true }} className={className}>
      e
    </LogoRoot>
  );
}

const EditorMarkRoot = styled('span', { name: 'KitEditorMark', slot: 'root' })<{ ownerState: EditorMarkLook }>({});

export type { EditorMarkPart };

export interface EditorMarkProps extends HTMLAttributes<HTMLSpanElement> {
  /** The piece of editor chrome to show. Its position and size come from `className` and `style`. */
  part: EditorMarkPart;
  /** Shows a bar in its chosen colour. */
  selected?: boolean;
}

/** Shows one piece of the template editor's chrome: selection, guides, safe zone and timeline marks.
 *  `data-part` names the piece. */
export function EditorMark({ part, selected = false, ...props }: EditorMarkProps) {
  return <EditorMarkRoot data-part={part} ownerState={{ part, selected }} {...props} />;
}
